import { useEffect, useState, useCallback } from 'react'

export type ThemeChoice = 'light' | 'dark' | 'system'
type ResolvedTheme = 'light' | 'dark'

const STORAGE_KEY = 'statiomart-theme'

const resolve = (choice: ThemeChoice): ResolvedTheme => {
  if (choice === 'system') {
    if (typeof window === 'undefined') return 'light'
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }
  return choice
}

const apply = (resolved: ResolvedTheme) => {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  root.classList.toggle('dark', resolved === 'dark')
  root.style.colorScheme = resolved
}

const readStored = (): ThemeChoice => {
  if (typeof window === 'undefined') return 'system'
  const v = window.localStorage.getItem(STORAGE_KEY)
  return v === 'light' || v === 'dark' || v === 'system' ? v : 'system'
}

export const initTheme = () => {
  if (typeof window === 'undefined') return
  apply(resolve(readStored()))
}

export const useTheme = () => {
  const [choice, setChoice] = useState<ThemeChoice>(readStored)
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolve(readStored()))

  useEffect(() => {
    const r = resolve(choice)
    setResolved(r)
    apply(r)
    if (typeof window === 'undefined') return
    if (window.localStorage.getItem(STORAGE_KEY) !== choice) {
      window.localStorage.setItem(STORAGE_KEY, choice)
    }
  }, [choice])

  useEffect(() => {
    if (choice !== 'system' || typeof window === 'undefined') return
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = () => {
      const r = mq.matches ? 'dark' : 'light'
      setResolved(r)
      apply(r)
    }
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [choice])

  // Cycle: light → dark → system → light. Lets users return to OS preference.
  const toggle = useCallback(() => {
    setChoice((prev) => (prev === 'light' ? 'dark' : prev === 'dark' ? 'system' : 'light'))
  }, [])

  return { choice, resolved, setChoice, toggle }
}
